import type { FC, MouseEvent } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HiPencilAlt, HiTrash } from "react-icons/hi";

import {
  Button,
  DialogForm,
  HelpLabel,
  InstantSearch,
  StandardTable,
  StoryblokHelpBlock,
  TableSkeleton,
} from "@components";
import { useCache } from "../../context/CacheContext";
import { deleteCompanyLogo } from "../../data/companyLogoStorage";
import { Company } from "../../models";

interface CompanyListProps {
  companies: Company[];
  isLoading: boolean;
  onCreate: () => void;
}

export const CompanyList: FC<CompanyListProps> = ({
  companies,
  isLoading,
  onCreate,
}) => {
  const { t } = useTranslation(["common", "company"]);
  const navigate = useNavigate();
  const { updateCacheKey } = useCache();
  const [companyToDelete, setCompanyToDelete] = useState<Company>();
  const [searchText, setSearchText] = useState("");
  const [filteredCompanies, setFilteredCompanies] =
    useState<Company[]>(companies);

  useEffect(() => {
    const query = searchText.trim().toLowerCase();
    setFilteredCompanies(
      query
        ? companies.filter((company) =>
            company.name.toLowerCase().includes(query),
          )
        : companies,
    );
  }, [companies, searchText]);

  const openCompany = (company: Company) => {
    navigate(`/workspace/companies/${company.id}`);
  };

  const tableRows = filteredCompanies.map((company) => ({
    id: company.id,
    onRowClickData: company,
    cells: [
      {
        id: "name",
        children: company.name || t("company:unknown_company"),
        showOnSmallScreen: true,
      },
      {
        id: "organizationNumber",
        children: company.organizationNumber || "",
        showOnSmallScreen: false,
      },
      {
        id: "contactEmail",
        children: company.contactEmail || "",
        showOnSmallScreen: false,
      },
    ],
    actions: [
      {
        id: "edit",
        icon: <HiPencilAlt />,
        onActionClick: (event: MouseEvent) => {
          event.stopPropagation();
          openCompany(company);
        },
      },
      {
        id: "delete",
        icon: <HiTrash />,
        color: "failure",
        onActionClick: (event: MouseEvent) => {
          event.stopPropagation();
          setCompanyToDelete(company);
        },
      },
    ],
  }));

  const tableHeaders = [
    {
      id: "name",
      children: t("common:company_name"),
      showOnSmallScreen: true,
    },
    {
      id: "organizationNumber",
      children: t("common:organization_number"),
      showOnSmallScreen: false,
    },
    {
      id: "contactEmail",
      children: t("common:contact_email"),
      showOnSmallScreen: false,
    },
  ];

  const handleDelete = async () => {
    if (!companyToDelete) return;
    try {
      await Company.remove(companyToDelete);
      try {
        await deleteCompanyLogo({
          storagePath: companyToDelete.logoStoragePath,
          workspaceId: companyToDelete.workspaceId,
          companyId: companyToDelete.id,
        });
      } catch (cleanupError) {
        console.warn(
          "Unable to remove the deleted company's logo",
          cleanupError,
        );
      }
      setCompanyToDelete(undefined);
      updateCacheKey();
    } catch (error) {
      console.error("Error deleting company:", error);
    }
  };

  return (
    <div className="overflow-hidden">
      <div className="mb-4 flex flex-col items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-text sm:text-2xl">
              {t("company:title")}
            </h1>
            <HelpLabel
              content={
                <StoryblokHelpBlock
                  storySlug="logibud-tms-web/help-popups/company"
                  accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
                />
              }
              className="mt-1"
            />
          </div>
          <p className="pb-2 pt-1 text-sm text-secondary sm:pb-4 sm:pt-2 sm:text-base">
            {t("company:description")}
          </p>
        </div>

        <div className="mb-4 flex w-full flex-col gap-3 md:flex-row">
          <div className="relative flex-grow">
            <InstantSearch
              placeholder={t("company:search_placeholder", {
                defaultValue: "Søk etter bedrift...",
              })}
              value={searchText}
              onChange={setSearchText}
            />
          </div>
          <div className="flex items-center">
            <Button variant="add" onClick={onCreate} size="md">
              {t("company:new_company")}
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : (
        <StandardTable
          hasActions
          rows={tableRows}
          header={tableHeaders}
          onRowClick={openCompany}
        />
      )}

      <DialogForm
        title={`${t("company:actions.delete_dialogTitle")} ${
          companyToDelete?.name || t("company:unknown_company")
        }?`}
        dismissible
        show={Boolean(companyToDelete)}
        toggleModal={() => setCompanyToDelete(undefined)}
        buttonConfirmColor="failure"
        confirmButton={handleDelete}
        buttonConfirmText={t("common:confirm")}
        buttonCloseText={t("common:close")}
      />
    </div>
  );
};
